import { AlertEnum, LayoutEnum, SituacaoEleicaoEnum } from '../enums';
import {
  Button,
  CargosCandidaturasSummaryCard,
  ComissaoEleitoralSummaryCard,
  DefaultContent,
  EleicaoSummaryCard,
} from '../components';
import {
  CargoResponseType,
  EleicaoResponseType,
  GrupoEleitoresResponseType,
  MembroComissaoEleicaoResponseType,
} from '../types';
import { FunctionComponent, useEffect, useState } from 'react';
import { useAlert, useAuthAxios, useErrorHandler, useErrorStatus, useLayout } from '../hooks';
import { useNavigate, useParams } from 'react-router-dom';

import { ApiHelper } from '../helpers';
import { AxiosPromise } from 'axios';
import GruposEleitoresSummaryCard from '../components/grupo-eleitores/GruposEleitoresSummaryCard';
import { Spinner } from '../utils';
import { StatusCodes } from 'http-status-codes';
import { useTranslation } from 'react-i18next';

const EleicaoView: FunctionComponent = () => {
  const { t } = useTranslation();
  const axios = useAuthAxios();
  const errorHandler = useErrorHandler();
  const navigate = useNavigate();
  const [eleicao, setEleicao] = useState<EleicaoResponseType>();
  const [loadingEleicao, setLoadingEleicao] = useState(true);
  const [membrosComissao, setMembrosComissao] = useState<MembroComissaoEleicaoResponseType[]>();
  const [loadingMembrosComissao, setLoadingMembrosComissao] = useState(true);
  const [cargos, setCargos] = useState<CargoResponseType[]>();
  const [loadingCargos, setLoadingCargos] = useState(true);
  const [gruposEleitores, setGruposEleitores] = useState<GrupoEleitoresResponseType[]>([]);
  const [loadingGruposEleitores, setLoadingGruposEleitores] = useState(true);
  let { idEleicao } = useParams();
  const { setErrorStatus } = useErrorStatus();
  const { createAlert } = useAlert();
  const permiteAlterarDadosEleicao = eleicao?.situacao === SituacaoEleicaoEnum.NAO_PUBLICADA.key;
  const { setLayout } = useLayout();

  useEffect(() => {
    setLayout(LayoutEnum.BASE_LAYOUT);
  }, []);

  useEffect(() => {
    if (idEleicao) {
      if (ApiHelper.isValidIntegerToApi(idEleicao)) {
        axios
          .get(ApiHelper.getUrl.apiSCE.eleicao(+idEleicao))
          .then((response) => {
            const dadosEleicao: EleicaoResponseType = response.data;
            setEleicao(dadosEleicao);
            if (
              dadosEleicao.situacao === SituacaoEleicaoEnum.PUBLICADA.key &&
              new Date(dadosEleicao.data_inicio) < new Date()
            ) {
              createAlert(
                `${t('texto.aviso_gerenciamento_eleicao_iniciada')}    
                  <a href="https://sigeleicao.ufpb.br" class="alert-link" target="_blank" rel="noreferrer">
                    ${t('label.sigeleicao')}
                  </a>
                `,
                AlertEnum.WARNING,
              );
            } else if (dadosEleicao.situacao === SituacaoEleicaoEnum.PUBLICADA.key) {
              createAlert(
                `${t('texto.aviso_gerenciamento_eleicao_publicada')}    
                  <a href="https://sigeleicao.ufpb.br" class="alert-link" target="_blank" rel="noreferrer">
                    ${t('label.sigeleicao')}
                  </a>
                `,
                AlertEnum.WARNING,
              );
            } else if (dadosEleicao.situacao === SituacaoEleicaoEnum.CANCELADA.key) {
              createAlert(t('texto.aviso_eleicao_cancelada'), AlertEnum.WARNING);
            }
            axios
              .get(ApiHelper.getUrl.apiSCE.membrosComissao(+idEleicao!))
              .then((response) => setMembrosComissao(response.data))
              .catch((error) => errorHandler(error, t('erro.recuperar_comissao')))
              .finally(() => setLoadingMembrosComissao(false));

            axios
              .get(ApiHelper.getUrl.apiSCE.cargos(+idEleicao!))
              .then((response) => {
                let cargos: CargoResponseType[] = response.data;
                let promises: AxiosPromise[] = [];

                cargos.forEach((cargo) =>
                  promises.push(
                    axios
                      .get(ApiHelper.getUrl.apiSCE.candidaturas(+idEleicao!, cargo.id))
                      .then((response) => (cargo.candidaturas = response.data)),
                  ),
                );

                Promise.all(promises).then(() => {
                  setCargos(cargos);
                });

                axios
                  .get(ApiHelper.getUrl.apiSCE.gruposEleitores(+idEleicao!))
                  .then((response) => setGruposEleitores(response.data))
                  .catch((error) => errorHandler(error, t('erro.recuperar_grupos_eleitores')))
                  .finally(() => setLoadingGruposEleitores(false));
              })
              .catch((error) => errorHandler(error, t('erro.recuperar_cargos')))
              .finally(() => setLoadingCargos(false));
          })
          .catch((error) => {
            if (error.response.status === StatusCodes.NOT_FOUND) {
              setErrorStatus(error.response.status);
            } else {
              errorHandler(error, t('erro.recuperar_eleicao'));
            }
          })
          .finally(() => setLoadingEleicao(false));
      } else {
        setTimeout(() => setErrorStatus(StatusCodes.NOT_FOUND));
      }
    }
  }, [idEleicao]);

  return loadingEleicao ? (
    <Spinner />
  ) : (
    <DefaultContent
      title={eleicao?.titulo!}
      pageAction={<Button title={t('label.voltar')} onClick={() => navigate('/eleicoes')} outline />}
    >
      <EleicaoSummaryCard
        domain={eleicao!}
        setState={setEleicao}
        idEleicao={eleicao!.id}
        permiteAlterarDadosEleicao={permiteAlterarDadosEleicao}
      />
      <ComissaoEleitoralSummaryCard
        domain={membrosComissao!}
        setState={setMembrosComissao}
        idEleicao={eleicao!.id}
        loading={loadingMembrosComissao}
        permiteAlterarDadosEleicao={permiteAlterarDadosEleicao}
      />
      <CargosCandidaturasSummaryCard
        domain={cargos!}
        setState={setMembrosComissao}
        idEleicao={eleicao!.id}
        loading={loadingCargos}
        permiteAlterarDadosEleicao={permiteAlterarDadosEleicao}
      />
      <GruposEleitoresSummaryCard
        domain={gruposEleitores!}
        setState={setGruposEleitores}
        idEleicao={eleicao!.id}
        loading={loadingGruposEleitores}
        permiteAlterarDadosEleicao={permiteAlterarDadosEleicao}
      />
    </DefaultContent>
  );
};

export default EleicaoView;
